import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Phone-width stand-in for a wide table: one row per record showing only the
// fields worth scanning, expanding on tap to reveal the rest. Pages keep their
// real table for md and up and render this below it inside a display:none box.
//
// records: [{
//   id, title, subtitle, badge?, muted?, details: [[label, value], ...],
//   onOpen?, openLabel?
// }]
function ExpandableRecordList({ records, emptyText = 'Nothing to show.' }) {
  const [expandedId, setExpandedId] = useState(null);

  if (!records.length) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
      </Paper>
    );
  }

  return (
    <Paper>
      {records.map((record, index) => {
        const open = expandedId === record.id;
        return (
          <Box key={record.id}>
            {index > 0 && <Divider />}
            <Box
              onClick={() => setExpandedId(open ? null : record.id)}
              role="button"
              aria-expanded={open}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1.25,
                cursor: 'pointer',
                opacity: record.muted ? 0.55 : 1,
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={700} noWrap>{record.title}</Typography>
                {record.subtitle && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {record.subtitle}
                  </Typography>
                )}
              </Box>
              {record.badge}
              <IconButton size="small" sx={{ p: 0.25 }} tabIndex={-1} aria-hidden>
                {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>

            <Collapse in={open} unmountOnExit>
              <Box sx={{ px: 1.5, pb: 1.5, backgroundColor: 'action.hover' }}>
                <Stack spacing={0.5} sx={{ pt: 1 }}>
                  {record.details.map(([label, value]) => (
                    <Stack key={label} direction="row" spacing={1} justifyContent="space-between" alignItems="baseline">
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
                      <Typography variant="caption" sx={{ textAlign: 'right', minWidth: 0 }}>{value}</Typography>
                    </Stack>
                  ))}
                </Stack>
                {record.onOpen && (
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    onClick={(e) => { e.stopPropagation(); record.onOpen(); }}
                    sx={{ mt: 1.5, textTransform: 'none' }}
                  >
                    {record.openLabel || 'Open'}
                  </Button>
                )}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Paper>
  );
}

export default ExpandableRecordList;
